import type { Placement } from '@popperjs/core';
import classNames from 'classnames';
import type { IntlKey } from 'locales';
import { FC, ReactNode, useRef, useState } from 'react';
import { Overlay, Tooltip } from 'react-bootstrap';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

interface InfoTooltipProps {
  children?: ReactNode;
  id: string;
  className?: string;
  tooltipPlacement?: Placement;
  tooltipKey?: IntlKey;
}

const StyledTooltip = styled(Tooltip)`
  & > .tooltip-inner {
    padding: 0;
  }
`;

export const InfoTooltip: FC<InfoTooltipProps> = ({ id, className, tooltipKey, tooltipPlacement, children }) => {
  const iconRef = useRef<HTMLElement>(null);
  const [isTooltipOpened, setIsTooltipOpened] = useState(false);
  const { formatMessage } = useIntl();
  const label = tooltipKey !== undefined ? formatMessage({ id: tooltipKey }) : undefined;

  const timeoutRef = useRef<NodeJS.Timeout>(null);
  const clearTimeoutRef = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  };

  const tooltipEnterHandler = () => {
    clearTimeoutRef();
    setIsTooltipOpened(true);
  };

  const tooltipLeaveHandler = () => {
    clearTimeoutRef();
    timeoutRef.current = setTimeout(() => setIsTooltipOpened(false), 100);
  };

  if (!label && !children) {
    return null;
  }

  return (
    <>
      <i
        ref={iconRef}
        id={id}
        className={classNames('icon ms-2 me-5 fs-12', className)}
        onMouseEnter={tooltipEnterHandler}
        onMouseLeave={tooltipLeaveHandler}
      >
        info_outline
      </i>
      <Overlay show={isTooltipOpened} target={iconRef.current} placement={tooltipPlacement ?? 'right'}>
        <StyledTooltip>
          <div className="bg-lvl1 border border-primary text-secondary bg-lvl1 p-3 text-start">
            {label ? <p>{label}</p> : <>{children}</>}
          </div>
        </StyledTooltip>
      </Overlay>
    </>
  );
};

InfoTooltip.displayName = 'InfoTooltip';
